/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from "@tanstack/react-query";
import { Api } from "../../../services/interceptor";
import { QUERY_KEY } from "../../../services/queryEndPoint";
import type { MatchBucket } from "../../tournamentPage/useTournamentMatches";

const STATUS: Record<MatchBucket, string> = { live: "IN_PROGRESS", up: "SCHEDULED", past: "ENDED" };
/** How many cards of each kind the league home carousel needs: every live match, the next fixtures, the latest results. */
const SIZE: Record<MatchBucket, number> = { live: 50, up: 20, past: 10 };

/** Set once the backend answers 404 for the league summary endpoint (older deployment) so we stop trying it. */
let leagueSummaryUnavailable = false;

/** Upcoming = scheduled from yesterday on, so fixtures that were never played or closed don't crowd out the real ones. */
const upcomingFrom = () => {
  const d = new Date(Date.now() - 86400000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const fetchLeague = async (leagueId: string, bucket: MatchBucket, size: number) => {
  const params = { leagueId, status: STATUS[bucket], page: 0, size, ...(bucket === "up" ? { fromDate: upcomingFrom() } : {}) };
  const res = await Api.get(QUERY_KEY.matchSummaryByLeagueIdAndStatusPageable, { params });
  return { list: (res?.data?.matchResponseList ?? []) as any[], hasNext: !!res?.data?.hasNext };
};

/**
 * Older backends: walk the league's tournaments with the per-tournament summary endpoint and merge.
 * Each card still carries its own tournament, so the carousel renders the same.
 */
const fetchByTournaments = async (leagueId: string, bucket: MatchBucket, size: number) => {
  const tRes = await Api.get(`${QUERY_KEY.getTournamentsByLeagues}/${leagueId}`);
  const tournaments: any[] = tRes?.data?.tournamentResponses ?? [];
  const lists = await Promise.all(
    tournaments.slice(0, 10).map((t) =>
      Api.get(QUERY_KEY.matchSummaryByTournamentIdAndStatusPageable, { params: { tournamentId: t.tournamentId, status: STATUS[bucket], page: 0, size } })
        .then((r) => ((r?.data?.matchResponseList ?? []) as any[]).map((m) => ({ ...m, tournament: m.tournament ?? { tournamentId: t.tournamentId, tournamentName: t.tournamentName, matchType: t.matchType, maxOvers: t.maxOvers } })))
        .catch(() => [] as any[]),
    ),
  );
  const ms = (m: any) => (m.startDate ? new Date(m.startDate).getTime() : 0);
  const from = new Date(`${upcomingFrom()}T00:00:00`).getTime();
  const all = lists.flat().filter((m) => bucket !== "up" || !m.startDate || ms(m) >= from).sort((a, b) => (bucket === "past" ? ms(b) - ms(a) : ms(a) - ms(b)));
  return { list: all.slice(0, size), hasNext: all.length > size };
};

const fetchBucket = async (leagueId: string, bucket: MatchBucket) => {
  const size = SIZE[bucket];
  if (!leagueSummaryUnavailable) {
    try {
      return await fetchLeague(leagueId, bucket, size);
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status !== 404 && status !== 405) throw e;
      leagueSummaryUnavailable = true;
    }
  }
  return fetchByTournaments(leagueId, bucket, size);
};

/** Live, upcoming and recent matches from every tournament in the league (league home carousel). */
export const useLeagueMatches = (leagueId?: string | null) => {
  const useBucket = (bucket: MatchBucket) =>
    useQuery({ queryKey: ["lhMatches", leagueId, bucket], queryFn: () => fetchBucket(leagueId as string, bucket), enabled: !!leagueId, staleTime: 30_000, refetchInterval: bucket === "live" ? 30_000 : false });
  const live = useBucket("live"), up = useBucket("up"), past = useBucket("past");
  return {
    live: live.data?.list ?? [], up: up.data?.list ?? [], past: past.data?.list ?? [],
    /** More results exist than the carousel shows. */
    morePast: !!past.data?.hasNext,
    loading: live.isLoading || up.isLoading || past.isLoading,
    error: live.isError && up.isError && past.isError,
    refetch: () => { void live.refetch(); void up.refetch(); void past.refetch(); },
  };
};
